// React and Router imports
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"

// Firebase imports
import { auth } from "../../firebase.js"

// Component imports
import WalkthroughSidebar from "./WalkthroughSidebar"
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";

// Styles
import "../../css/Walkthrough.css"
import "../../css/DocumentationHomeComponent.css"


function Walkthrough() {

    const [loading, setLoading] = useState(true)
    const [loadingMessage, setLoadingMessage] = useState('Loading your walkthroughs...');
    const [uid, setUid] = useState("")
    const [userEmail, setUserEmail] = useState("")
    const navigate = useNavigate()
    const { enhancedRightClickEnabled } = useQuickLinks();
    const [hasTutorials, setHasTutorials] = useState(false)

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid)
                setUserEmail(user.email)
            } else {
                setUid(null)
                setUserEmail(null)
                setLoading(false); // <-- Stop loading if not signed in
            }
        })
        return () => { unsubscribe() }
    }, [])

    const loggedOutOptions = [
        {
            label: "Login",
            icon: <i className="fas fa-sign-in-alt"></i>,
            onClick: () => navigate("/login")
        },
        {
            label: "Register",
            icon: <i className="fas fa-user-plus"></i>,
            onClick: () => navigate("/register")
        },
        {
            label: "Try My Notebook",
            icon: <i className="fas fa-book"></i>,
            onClick: () => navigate("/my-notebook")
        },
        {
            label: "Visit YouTube Channel",
            icon: <i className="fab fa-youtube"></i>,
            onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer")
        },
        {
            label: "Join Discord",
            icon: <i className="fab fa-discord"></i>,
            onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer")
        }
    ];

    // Context menu setup
    const contextMenuOptions = [
        {
            label: "Your Documents",
            url: "/documentation",
            onClick: () => navigate("/documentation"),
        },
        {
            label: "Your Walkthroughs",
            url: "/walkthroughs",
            onClick: () => navigate("/walkthroughs")
        },
        {
            label: "Your Notebook",
            url: "/my-notebook",
            onClick: () => navigate("/my-notebook")
        },
        {
            label: "Your Workspace",
            url: "/workspaces",
            onClick: () => navigate("/workspaces")
        },
        {
            label: "Account Settings",
            url: `/account/${uid}`,
            onClick: () => navigate(`/account/${uid}`)
        }
    ];

    // Use loggedOutOptions if not logged in, otherwise contextMenuOptions
    const menuOptions = uid ? contextMenuOptions : loggedOutOptions;
    const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

    // Theme cleanup on unmount



    useEffect(() => { if (uid) { fetchTutorialDetails() } }, [uid])

    // Optionally update loading message at different stages
    useEffect(() => {
        if (loading) {
            setLoadingMessage('Loading your walkthroughs...');
        }
    }, [loading]);

    const fetchTutorialDetails = async () => {
        try {
            setLoadingMessage('Authenticating user...');
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;
            if (!token) {
                setLoadingMessage('User is not authenticated.');
                console.error("User is not authenticated.");
                return;
            }
            if (uid.length !== 0) {
                setLoadingMessage('Fetching your walkthroughs...');
                const response = await fetch("https://bluebirddocumentationadmin.pythonanywhere.com/walkthrough/" + uid + "/", {
                    method: "GET",
                    headers: {
                        "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                        'requesting-user-uid': uid,
                        'requesting-user-email': userEmail,
                    }
                })
                if (response.status === 200) {
                    setLoadingMessage('Walkthroughs loaded!');
                    setHasTutorials(true)
                    setLoading(false)
                } else if (response.status === 404) {
                    setLoadingMessage('No walkthroughs found.');
                    console.log("No tutorials found for this author.")
                    setHasTutorials(false)
                    setLoading(false)
                } else {
                    setLoadingMessage('Failed to fetch walkthroughs.');
                    console.log("Failed to fetch tutorials for this author.")
                }
            }
        } catch (error) {
            setLoadingMessage('Error loading walkthroughs.');
            console.error("Error in fetching tutorials for this author: ", error)
        }
    }

    const handleCreateClick = () => { navigate("/walkthroughs/create") }

    // Conditional rendering for not signed in
    if (!uid && !loading) {
        return (
            <div
                className="Walkthrough__div"
                onContextMenu={
                    enhancedRightClickEnabled
                        ? (e) => {
                            e.preventDefault();
                            showContextMenu(e.clientX, e.clientY);
                        }
                        : undefined
                }
            >
                <div className="Walkthrough__main-content">
                    <p className="large">It looks like you're not signed in.</p>
                    <p className="medium">
                        <strong>
                            To view or create walkthroughs, you must have an account with us.
                        </strong>
                        {" "}If you already have an account, you can login below. If not, register for free!
                    </p>
                    <button
                        onClick={() => navigate("/")}
                        style={{ marginRight: "10px" }}
                        className="ConflictResolver__button"
                    >
                        Home
                    </button>
                    <button
                        onClick={() => navigate("/login")}
                        style={{ marginRight: "10px" }}
                        className="ConflictResolver__button"
                    >
                        Login
                    </button>
                    <button
                        onClick={() => navigate("/register")}
                        className="ConflictResolver__button"
                    >
                        Register
                    </button>
                </div>
                {enhancedRightClickEnabled && ContextMenuComponent}
            </div>
        );
    }

    return (
        <div 
            className="Walkthrough__div"
            onContextMenu={enhancedRightClickEnabled ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
            } : undefined}
        >
            <WalkthroughSidebar />

            <div className="Walkthrough__main-content">
                {loading ? (
                    <div className="DocumentationHomeComponent__coffee-loader-div">
                            <div className="coffee-cup-loader">
                                    <div className="cup">
                                            <div className="cup-body"></div>
                                            <div className="cup-handle"></div>
                                            <div className="cup-base"></div>
                                            <div className="cup-shadow"></div>
                                            <div className="steam steam1"></div>
                                            <div className="steam steam2"></div>
                                            <div className="steam steam3"></div>
                                    </div>
                            </div>
                            <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
                    </div>
                ) : loading === false && hasTutorials === false ? (
                    <div>
                        <p className="large">
                            Looks like you haven't created any walkthroughs yet! <strong>Click the button below to get started</strong>.
                        </p>
                        <button onClick={handleCreateClick} className="submit-button">Create</button>
                    </div>
                ) : (
                    <div>
                        <p className="large">
                            <strong>To view/modify existing walkthroughs</strong>, click on any of the walkthroughs on the left sidebar.
                        </p>
                        <p className="large" style={{ paddingTop: "50px" }}>
                            <strong>To create a new walkthrough</strong>, click the green "Create" button on the bottom of the left sidebar.
                        </p>
                    </div>
                )}
            </div>
            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    )

}

export default Walkthrough